import { Component, createSignal } from "solid-js";
import { useCheckoutFlow } from "@/contexts/checkout-flow/checkout-flow.provider";
import { MONTH_NAMES } from "@/shared/constants/date.const";
import { cancelCampaign } from "@/services/checkout.service";
import { CancellationSuccessModal } from "../cancellation-success-modal/CancellationSuccessModal";
import { MODAL_EXIT_DELAY_MS } from "@/shared/constants/event.const";
import {
  publishCancelCampaignModalKeepClick,
  publishCancelCampaignModalCancelClick,
} from "@/services/analytics.service";

interface ConfirmCancelModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ConfirmCancelModal: Component<ConfirmCancelModalProps> = (
  props,
) => {
  const {
    cancelCampaign: cancelCampaignData,
    dealerId,
    exitFeaturedDealer,
    setShowGlobalError,
  } = useCheckoutFlow();
  const campaign = cancelCampaignData();
  const [showSuccessModal, setShowSuccessModal] = createSignal(false);
  const [isLoading, setIsLoading] = createSignal(false);

  const timeframe = campaign
    ? `${MONTH_NAMES[campaign.month]} ${campaign.year}`
    : "this timeframe";

  const handleCancelReservation = async () => {
    if (!campaign) return;

    setIsLoading(true);
    try {
      const campaignMonth = `${campaign.year}-${String(campaign.month).padStart(2, "0")}`;
      await cancelCampaign(dealerId(), { campaignMonth });
      setShowSuccessModal(true);
    } catch {
      setShowGlobalError(true);
      props.onClose(); // Close the cancel modal when error occurs
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setTimeout(() => {
      exitFeaturedDealer();
    }, MODAL_EXIT_DELAY_MS);
  };

  return (
    <>
      <fd-mfe-modal-dialog prop:open={props.isOpen && !showSuccessModal()}>
        <h2 slot="modal-dialog-header">Are you sure?</h2>
        <p slot="modal-dialog-body">
          You are about to cancel your Featured Dealer reservation for{" "}
          {timeframe}. Rebooking the same or a similar slot after cancellation
          may not be possible.
        </p>
        <fd-mfe-modal-dialog-footer slot="modal-dialog-footer">
          <fd-mfe-button
            onClick={() => {
              publishCancelCampaignModalCancelClick();
              handleCancelReservation();
            }}
            prop:variant="critical"
            prop:disabled={isLoading()}
            data-testid="confirm-cancel-button"
          >
            {isLoading() ? "Canceling..." : "Cancel reservation"}
          </fd-mfe-button>
          <fd-mfe-button
            onClick={() => {
              publishCancelCampaignModalKeepClick();
              props.onClose();
            }}
            prop:rank="secondary"
            prop:disabled={isLoading()}
          >
            Keep reservation
          </fd-mfe-button>
        </fd-mfe-modal-dialog-footer>
      </fd-mfe-modal-dialog>
      <CancellationSuccessModal
        isOpen={showSuccessModal()}
        onClose={handleSuccessModalClose}
      />
    </>
  );
};
